import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import rawAxios from 'axios';
import interceptedAxios from '../app/axiosInterceptor';
import { messageToastType_const, STATUS_API } from '../app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import { showGlobalToast } from './uiSlice';

/**
 * Dữ liệu bản đồ thu thập từ HERE.
 *
 * Gọi qua CMS (`/osm/here/*`) chứ KHÔNG gọi thẳng osm_service: dịch vụ đó chạy trong cluster
 * nội bộ, trình duyệt không tới được. Đi qua CMS thì dùng luôn `axiosInterceptor` — cùng origin,
 * cùng token, tự xử lý 401 — nên ở đây không cần base URL hay client riêng.
 *
 * Slice này KHÔNG có lệnh điều khiển thu thập (seed/start/pause): việc đó tiêu quota HERE và
 * chạy nền bằng cron, không phải thao tác của người dùng CMS.
 */
/**
 * Đường mặc định: qua CMS. Đặt `REACT_APP_OSM_SERVICE_URL` thì gọi THẲNG osm_service — chỉ dùng
 * khi chạy máy cá nhân, lúc CMS chưa dựng. osm_service bật CORS và nhóm `/here/*` chưa gắn xác
 * thực nên gọi thẳng được; trên môi trường thật KHÔNG khai biến này, vì dịch vụ chỉ nghe trong
 * cluster và lớp kiểm quyền nằm ở CMS.
 */
const DIRECT_URL = (process.env.REACT_APP_OSM_SERVICE_URL || '').replace(/\/+$/, '');

// Bản build production KHÔNG bao giờ được đi đường tắt, kể cả khi biến còn sót trong .env: địa chỉ
// đó là máy của người dựng, nên gói build ra sẽ bảo TRÌNH DUYỆT CỦA KHÁCH gọi vào localhost của
// chính họ. Hỏng thầm lặng và chỉ lộ ra khi đã lên tay người dùng, nên chặn ngay tại đây.
const DIRECT_BASE = process.env.NODE_ENV === 'production' ? '' : DIRECT_URL;
if (DIRECT_URL && !DIRECT_BASE) {
  // eslint-disable-next-line no-console
  console.warn('[here] Bỏ qua REACT_APP_OSM_SERVICE_URL trong bản build production, dùng cổng CMS /osm/here.');
}

const PREFIX = DIRECT_BASE ? `${DIRECT_BASE}/here` : '/osm/here';

// Gọi thẳng thì dùng axios trần: instance có interceptor ghim baseURL của CMS và sẽ đá về
// trang đăng nhập khi gặp 401 — cả hai đều sai khi đích là osm_service.
const axios = DIRECT_BASE ? rawAxios : interceptedAxios;

/**
 * Khoá của đường gọi thẳng. osm_service đứng sau nginx có lớp kiểm khoá, nên đường tắt phải mang
 * theo `X-Api-Key`; đi qua CMS thì không cần vì CMS tự gắn ở phía máy chủ.
 *
 * Khoá này CHỈ dành cho máy cá nhân. Nó nằm trong gói build nếu ai đó khai biến lúc dựng bản phát
 * hành — mà gói build thì ai xem mã nguồn trang cũng đọc được. Chốt chặn `NODE_ENV === production`
 * ở trên đã vô hiệu cả đường tắt lẫn khoá này trong bản phát hành.
 */
const DIRECT_KEY = DIRECT_BASE ? process.env.REACT_APP_OSM_SERVICE_KEY || '' : '';
if (DIRECT_KEY) {
  rawAxios.defaults.headers.common['X-Api-Key'] = DIRECT_KEY;
}

const errorText = error => {
  const data = error?.response?.data;

  // Lý do THẬT từ phía dưới luôn thắng mọi suy diễn theo mã lỗi.
  //
  // Bản cũ đặt nhánh 404 lên trước và trả một câu cố định "CMS chưa có dịch vụ bản đồ". Ngày
  // 17-18/09/2026 câu đó làm cả người vận hành lẫn người sửa đuổi nhầm suốt nhiều giờ: cổng vẫn
  // chạy, chỉ thiếu đúng một route, nhưng màn hình lại đổ cho hạ tầng chưa triển khai.
  //
  // osm_service trả khoá `messages` (SỐ NHIỀU), có lúc là chuỗi có lúc là mảng; cms_api trả
  // `message`. Đọc thiếu một trong hai là nuốt mất lý do thật và rơi xuống câu chung chung.
  const fromBody =
    data?.message ||
    (Array.isArray(data?.messages) ? data.messages.filter(Boolean).join('; ') : data?.messages) ||
    data?.error;
  if (fromBody) return String(fromBody);

  // 404 mà phía dưới KHÔNG kèm thông báo nào: nhiều khả năng cổng CMS thiếu route tương ứng, ví dụ
  // giao diện đã đổi đường gọi mà bản CMS đang chạy chưa mở theo. Nói là "có thể" chứ không khẳng
  // định, vì 404 cũng có thể chỉ là không tìm thấy dữ liệu.
  if (error?.response?.status === 404 && !DIRECT_BASE) {
    return 'Không tìm thấy đường gọi dịch vụ bản đồ (404). Có thể bản CMS đang chạy thiếu route tương ứng dưới /osm/here.';
  }

  return error?.message || 'Không gọi được dịch vụ bản đồ';
};

/** osm_service bọc response trong { data }; CMS trả nguyên xi nên phải bóc ở đây. */
const unwrap = res => (res?.data && Object.prototype.hasOwnProperty.call(res.data, 'data') ? res.data.data : res?.data);

/**
 * Toast chỉ bật cho hành động GHI; lệnh đọc danh sách thì im lặng.
 *
 * `successMessage` nhận cả hàm: mấy lệnh như dò/áp dụng trả về con số đếm được, và câu "đã xong"
 * chung chung sẽ nói dối khi con số đó bằng 0 — người dùng nhìn xuống bảng trống mà tưởng lỗi.
 */
const makeThunk = (name, request, { successMessage, errorMessage } = {}) =>
  createAsyncThunk(`hereSlice/${name}`, async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await request(payload ?? {});
      const data = unwrap(res);
      if (successMessage) {
        // Hàm được trả về `{ message, type }` khi phản hồi 200 nhưng nội dung là hỏng — dịch vụ
        // báo "đẩy thất bại, đã ghi lại để thử lại" bằng mã 200 chứ không ném lỗi.
        const result = typeof successMessage === 'function' ? successMessage(data) : successMessage;
        const { message, type } = typeof result === 'string' ? { message: result } : result ?? {};
        if (message) dispatch(showGlobalToast({ message, type: type || messageToastType_const.success }));
      }
      return data;
    } catch (error) {
      if (errorMessage) {
        dispatch(
          showGlobalToast({ message: `${errorMessage}: ${errorText(error)}`, type: messageToastType_const.error })
        );
      }
      return rejectWithValue(errorText(error));
    }
  });

// ───────────────────────── dữ liệu đã thu thập ─────────────────────────

export const getHereStatus = makeThunk('getHereStatus', () => axios.get(`${PREFIX}/status`));

export const getHereAddresses = makeThunk('getHereAddresses', params =>
  axios.get(`${PREFIX}/addresses`, {
    params: {
      page: params.page || 1,
      limit: params.limit || 20,
      province: params.province || undefined,
      street: params.street || undefined,
      q: params.q || undefined
    }
  })
);

// ───────────────────────── đẩy sang server bản đồ ─────────────────────────

export const getHereSyncTargets = makeThunk('getHereSyncTargets', () => axios.get(`${PREFIX}/sync/targets`));

export const createHereSyncTarget = makeThunk(
  'createHereSyncTarget',
  payload => axios.post(`${PREFIX}/sync/targets`, payload),
  { successMessage: MESSAGE.HERE_TARGET_CREATE_SUCCESS, errorMessage: MESSAGE.HERE_TARGET_CREATE_FAIL }
);

export const updateHereSyncTarget = makeThunk(
  'updateHereSyncTarget',
  ({ id, ...payload }) => axios.patch(`${PREFIX}/sync/targets/${id}`, payload),
  { successMessage: MESSAGE.HERE_TARGET_UPDATE_SUCCESS, errorMessage: MESSAGE.HERE_TARGET_UPDATE_FAIL }
);

export const deleteHereSyncTarget = makeThunk(
  'deleteHereSyncTarget',
  id => axios.delete(`${PREFIX}/sync/targets/${id}`),
  { successMessage: MESSAGE.HERE_TARGET_DELETE_SUCCESS, errorMessage: MESSAGE.HERE_TARGET_DELETE_FAIL }
);

export const pushHereSyncTarget = makeThunk(
  'pushHereSyncTarget',
  id => axios.post(`${PREFIX}/sync/targets/${id}/push`),
  { successMessage: MESSAGE.HERE_PUSH_SUCCESS, errorMessage: MESSAGE.HERE_PUSH_FAIL }
);

export const pushHereAllTargets = makeThunk('pushHereAllTargets', () => axios.post(`${PREFIX}/sync/push`), {
  successMessage: MESSAGE.HERE_PUSH_SUCCESS,
  errorMessage: MESSAGE.HERE_PUSH_FAIL
});

// ───────────────────────── duyệt sửa địa chỉ ─────────────────────────

/**
 * Vòng đời một đề xuất: `pending` → người duyệt → `approved` → áp dụng → `applied`.
 * Chỉ `approved` mới đẩy sang server bản đồ được — ràng buộc đó nằm ở dịch vụ, không ở đây.
 */
export const getHereEdits = makeThunk('getHereEdits', params =>
  axios.get(`${PREFIX}/edits`, {
    params: {
      page: params.page || 1,
      limit: params.limit || 20,
      targetId: params.targetId || undefined,
      status: params.status || undefined,
      street: params.street || undefined
    }
  })
);

export const getHereEditStats = makeThunk('getHereEditStats', params =>
  axios.get(`${PREFIX}/edits/stats`, { params: { targetId: params.targetId || undefined } })
);

export const generateHereEdits = makeThunk(
  'generateHereEdits',
  ({ targetId, limit }) => axios.post(`${PREFIX}/edits/generate/${targetId}`, { limit: limit || 50 }),
  {
    // Nói thẳng con số. "Đã dò xong" mà bảng trống thì người dùng không biết là hết việc hay hỏng.
    successMessage: data => {
      if (!data) return MESSAGE.HERE_EDIT_GENERATE_SUCCESS;
      if (!data.scanned) return MESSAGE.HERE_EDIT_GENERATE_NOTHING_LEFT;
      if (!data.created) {
        return `Đã soi ${data.scanned} địa chỉ, không có đề xuất mới (server đích đã có số nhà: ${data.skippedRemoteHasHouse}, không tra được: ${data.skippedNoRemote})`;
      }
      return `Đã tạo ${data.created} đề xuất mới trên ${data.scanned} địa chỉ soi`;
    },
    errorMessage: MESSAGE.HERE_EDIT_GENERATE_FAIL
  }
);

// Dịch vụ chỉ đổi những dòng đang CHỜ DUYỆT; số đổi được có thể ít hơn số đã chọn nếu ai đó vừa
// xử lý cùng lúc, nên hiện đúng con số dịch vụ trả về.
export const approveHereEdits = makeThunk('approveHereEdits', ids => axios.post(`${PREFIX}/edits/approve`, { ids }), {
  successMessage: data => `${MESSAGE.HERE_EDIT_APPROVE_SUCCESS} ${data?.changed ?? 0} đề xuất`,
  errorMessage: MESSAGE.HERE_EDIT_APPROVE_FAIL
});

export const rejectHereEdits = makeThunk('rejectHereEdits', ids => axios.post(`${PREFIX}/edits/reject`, { ids }), {
  successMessage: data => `${MESSAGE.HERE_EDIT_REJECT_SUCCESS} ${data?.changed ?? 0} đề xuất`,
  errorMessage: MESSAGE.HERE_EDIT_REJECT_FAIL
});

// Duyệt/từ chối TẤT CẢ đề xuất khớp bộ lọc, không chỉ trang đang xem. Gửi bộ lọc thay vì danh
// sách id: danh sách có thể dài hơn giới hạn 500 id của endpoint kia.
export const approveAllHereEdits = makeThunk(
  'approveAllHereEdits',
  params => axios.post(`${PREFIX}/edits/approve-all`, params),
  {
    successMessage: data => `${MESSAGE.HERE_EDIT_APPROVE_SUCCESS} ${data?.changed ?? 0} đề xuất`,
    errorMessage: MESSAGE.HERE_EDIT_APPROVE_FAIL
  }
);

export const rejectAllHereEdits = makeThunk('rejectAllHereEdits', params => axios.post(`${PREFIX}/edits/reject-all`, params), {
  successMessage: data => `${MESSAGE.HERE_EDIT_REJECT_SUCCESS} ${data?.changed ?? 0} đề xuất`,
  errorMessage: MESSAGE.HERE_EDIT_REJECT_FAIL
});

// Đẩy lên MỌI server bản đồ đang bật, không riêng server đang chọn: nội dung ghi đè là
// (hereId, số nhà, toạ độ) — không phụ thuộc server nào, nên số nhà đã duyệt thì đúng với mọi bản
// đồ. Bắt bấm lại cho từng server chỉ làm các bản đồ lệch nhau tuỳ ai nhớ bấm đủ.
export const applyHereEdits = makeThunk('applyHereEdits', () => axios.post(`${PREFIX}/edits/apply`), {
  // Dịch vụ có thể trả về "đẩy thất bại" trong một phản hồi 200 (bản ghi chuyển sang trạng thái
  // lỗi, giữ lại để thử lại) — bắt nhánh đó ra chứ đừng báo thành công.
  successMessage: data => {
    if (data?.failed) {
      return {
        message: `${MESSAGE.HERE_EDIT_APPLY_FAIL}: ${data.error || `${data.failed} dòng lỗi`}`,
        type: messageToastType_const.error
      };
    }
    if (!data?.applied) return MESSAGE.HERE_EDIT_APPLY_NOTHING;
    const servers = data.targets?.length ?? 1;
    return `${MESSAGE.HERE_EDIT_APPLY_SUCCESS} (${data.applied} địa chỉ × ${servers} server)`;
  },
  errorMessage: MESSAGE.HERE_EDIT_APPLY_FAIL
});

export const revertHereEdit = makeThunk('revertHereEdit', id => axios.post(`${PREFIX}/edits/${id}/revert`), {
  successMessage: MESSAGE.HERE_EDIT_REVERT_SUCCESS,
  errorMessage: MESSAGE.HERE_EDIT_REVERT_FAIL
});


// ───────────────────────── ghi đè đơn vị hành chính ─────────────────────────

/**
 * Sửa cấp xã/phường cho một VÙNG, khi chính OSM vẽ sai ranh giới.
 *
 * Khác hẳn khối duyệt số nhà bên trên: dữ liệu KHÔNG đến từ HERE. HERE sai toàn bộ 42/42 điểm về
 * cấp hành chính sau sáp nhập, nên nó không được phép chạm vào phần này. Đây là chỗ người vận
 * hành nhập tay một quyết định có căn cứ pháp lý.
 */
export const getAdminOverrides = makeThunk('getAdminOverrides', () => axios.get(`${PREFIX}/admin-overrides`));

export const lookupOsmGeometry = makeThunk(
  'lookupOsmGeometry',
  ({ osmType, osmId }) => axios.get(`${PREFIX}/admin-overrides/osm-lookup`, { params: { osmType, osmId } }),
  { errorMessage: MESSAGE.ADMIN_OVERRIDE_LOOKUP_FAIL }
);

export const createAdminOverride = makeThunk('createAdminOverride', payload => axios.post(`${PREFIX}/admin-overrides`, payload), {
  successMessage: MESSAGE.ADMIN_OVERRIDE_SAVE_SUCCESS,
  errorMessage: MESSAGE.ADMIN_OVERRIDE_SAVE_FAIL
});

export const applyAdminOverride = makeThunk(
  'applyAdminOverride',
  ({ id, targetId }) => axios.post(`${PREFIX}/admin-overrides/${id}/apply/${targetId}`),
  { successMessage: MESSAGE.ADMIN_OVERRIDE_APPLY_SUCCESS, errorMessage: MESSAGE.ADMIN_OVERRIDE_APPLY_FAIL }
);

export const revertAdminOverride = makeThunk('revertAdminOverride', id => axios.post(`${PREFIX}/admin-overrides/${id}/revert`), {
  successMessage: MESSAGE.ADMIN_OVERRIDE_REVERT_SUCCESS,
  errorMessage: MESSAGE.ADMIN_OVERRIDE_REVERT_FAIL
});

const initialState = {
  status: null,
  addresses: [],
  addressesMeta: { total: 0, page: 1, limit: 20 },
  targets: [],
  edits: [],
  editsMeta: { total: 0, page: 1, limit: 20 },
  editStats: {},
  error: null,
  statusGetStatus: null,
  statusGetAddresses: null,
  statusGetTargets: null,
  statusMutateTarget: null,
  statusPush: null,
  statusGetEdits: null,
  statusGetEditStats: null,
  statusGenerateEdits: null,
  statusDecideEdits: null,
  statusApplyEdits: null,
  adminOverrides: [],
  osmLookup: null,
  statusGetAdminOverrides: null,
  statusLookupOsm: null,
  statusMutateAdminOverride: null
};

/** Gắn 3 reducer pending/fulfilled/rejected cho một thunk vào một khoá trạng thái. */
const attach = (builder, thunk, statusKey, onSuccess) =>
  builder
    .addCase(thunk.pending, state => {
      state[statusKey] = STATUS_API.PENDING;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[statusKey] = STATUS_API.SUCCESS;
      if (onSuccess) onSuccess(state, action);
    })
    .addCase(thunk.rejected, (state, action) => {
      state[statusKey] = STATUS_API.FAILURE;
      state.error = action.payload;
    });

export const hereSlice = createSlice({
  name: 'hereSlice',
  initialState,
  reducers: {
    clearOsmLookup: state => {
      state.osmLookup = null;
    },
    resetHereStatus: state => {
      state.statusMutateTarget = null;
      state.statusPush = null;
      state.statusGenerateEdits = null;
      state.statusDecideEdits = null;
      state.statusApplyEdits = null;
    }
  },
  extraReducers: builder => {
    attach(builder, getHereStatus, 'statusGetStatus', (state, action) => {
      state.status = action.payload;
    });
    attach(builder, getHereAddresses, 'statusGetAddresses', (state, action) => {
      state.addresses = action.payload?.items ?? [];
      state.addressesMeta = action.payload?.meta ?? initialState.addressesMeta;
    });
    attach(builder, getHereSyncTargets, 'statusGetTargets', (state, action) => {
      state.targets = Array.isArray(action.payload) ? action.payload : [];
    });
    attach(builder, createHereSyncTarget, 'statusMutateTarget');
    attach(builder, updateHereSyncTarget, 'statusMutateTarget');
    attach(builder, deleteHereSyncTarget, 'statusMutateTarget');
    attach(builder, pushHereSyncTarget, 'statusPush');
    attach(builder, pushHereAllTargets, 'statusPush');
    attach(builder, getHereEdits, 'statusGetEdits', (state, action) => {
      state.edits = action.payload?.items ?? [];
      state.editsMeta = action.payload?.meta ?? initialState.editsMeta;
    });
    attach(builder, getHereEditStats, 'statusGetEditStats', (state, action) => {
      state.editStats = action.payload ?? {};
    });
    attach(builder, generateHereEdits, 'statusGenerateEdits');
    attach(builder, approveHereEdits, 'statusDecideEdits');
    attach(builder, approveAllHereEdits, 'statusDecideEdits');
    attach(builder, rejectAllHereEdits, 'statusDecideEdits');
    attach(builder, rejectHereEdits, 'statusDecideEdits');
    attach(builder, applyHereEdits, 'statusApplyEdits');
    attach(builder, revertHereEdit, 'statusApplyEdits');
    attach(builder, getAdminOverrides, 'statusGetAdminOverrides', (state, action) => {
      state.adminOverrides = Array.isArray(action.payload) ? action.payload : [];
    });
    attach(builder, lookupOsmGeometry, 'statusLookupOsm', (state, action) => {
      state.osmLookup = action.payload ?? null;
    });
    attach(builder, createAdminOverride, 'statusMutateAdminOverride');
    attach(builder, applyAdminOverride, 'statusMutateAdminOverride');
    attach(builder, revertAdminOverride, 'statusMutateAdminOverride');
  }
});

export const { resetHereStatus, clearOsmLookup } = hereSlice.actions;
export default hereSlice.reducer;
